import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { app } from "./firebaseClient";
import { userApiService } from "./userApiService";
import { isIosDevice, isMobileDevice } from "../utils/pwaUtils";
import { toast } from "sonner";

let messagingInstance = null;
let foregroundListenerAttached = false;

function getVapidKey() {
  return process.env.REACT_APP_FIREBASE_VAPID_KEY || "";
}

async function getMessagingInstance() {
  if (messagingInstance) return messagingInstance;
  const supported = await isSupported();
  if (!supported) return null;
  messagingInstance = getMessaging(app);
  return messagingInstance;
}

function attachForegroundListener(messaging) {
  if (foregroundListenerAttached) return;
  foregroundListenerAttached = true;
  onMessage(messaging, (payload) => {
    const title = payload.notification?.title || "Smart Farming";
    const body = payload.notification?.body || "";
    toast.info(body || title, {
      description: body ? title : undefined,
      duration: 6000,
    });
  });
}

export async function registerMobilePush() {
  if (!isMobileDevice()) return false;
  if (!("Notification" in window) || !("serviceWorker" in navigator)) return false;

  const vapidKey = getVapidKey();
  if (!vapidKey) return false;

  if (Notification.permission === "denied") return false;

  if (Notification.permission === "default") {
    const result = await Notification.requestPermission();
    if (result !== "granted") return false;
  }

  const messaging = await getMessagingInstance();
  if (!messaging) return false;

  const registration = await navigator.serviceWorker.ready;
  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: registration,
  });

  if (!token) return false;

  const platform = isIosDevice() ? "ios" : "android";
  await userApiService.registerPushToken({ token, platform });
  attachForegroundListener(messaging);
  return true;
}

export async function initMobilePush(user) {
  if (!user || !isMobileDevice()) return;

  try {
    if (Notification.permission === "granted") {
      await registerMobilePush();
      return;
    }

    if (Notification.permission !== "default") return;

    const promptKey = "sf-push-prompt-shown";
    if (sessionStorage.getItem(promptKey)) return;
    sessionStorage.setItem(promptKey, "1");

    toast.info("Enable alerts on your phone", {
      description: "Get pump, offline, and critical alerts like other apps.",
      duration: 8000,
      action: {
        label: "Enable",
        onClick: () => {
          registerMobilePush().catch(() => {});
        },
      },
    });
  } catch {
    // Push is optional — never block the app
  }
}

export async function unregisterMobilePush() {
  const vapidKey = getVapidKey();
  if (!vapidKey) return;

  try {
    const messaging = await getMessagingInstance();
    if (!messaging) return;
    const registration = await navigator.serviceWorker.ready;
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });
    if (token) {
      await userApiService.unregisterPushToken({ token });
    }
  } catch {
    // ignore
  }
}
